<template>
    <div
        class="custom-upload-wrapper"
        :class="[{ 'has-file': file }, { isNotrequired: !required }, classContainerAdd, { 'is-cropper-open': showCropper }]"
    >
        <!-- Label -->
        <BaseLabel
            v-if="label"
            :customClass="customClassLabel"
            :customSizeLabel="customSizeLabel"
            :label="label"
            :required="required"
        >
            <template #label-icon>
                <slot name="label-icon"></slot>
            </template>
        </BaseLabel>
        <p v-if="description" class="ppv2-text-left ppv2-text-xs ppv2-text-gray-500 ppv2-mb-2 mb-2">
            {{ $t(description) }}
        </p>

        <!-- Zone de drop / upload -->
        <div
            class="upload-box d-flex flex-column justify-content-center align-items-center"
            :class="{
                'drag-over': dragOver,
                'is-disabled': disabled,
                borderValid: state === 'valid',
                borderError: state === 'error',
                computedState,
            }"
            @click="selectFile"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
        >
            <!-- Image preview (only for non-PDF images/videos) -->
            <img v-if="preview && !isPdf && !isVideo" :src="preview" alt="preview" class="preview-image" />

            <!-- Video preview -->
            <video v-else-if="preview && isVideo" :src="preview" class="preview-image" autoplay loop muted playsinline></video>

            <!-- PDF preview icon -->
            <div v-else-if="isPdf" class="pdf-icon-wrapper ppv2-flex ppv2-flex-col ppv2-items-center">
                <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="ppv2-text-red-500"
                >
                    <path
                        d="M7 18H17V16H7V18ZM7 14H17V12H7V14ZM7 10H13V8H7V10ZM14 2H6C4.89 2 4 2.89 4 4V20C4 21.1 4.89 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"
                        fill="currentColor"
                    />
                </svg>
                <span class="ppv2-text-[10px] ppv2-mt-1 ppv2-text-black">PDF</span>
            </div>

            <!-- Icône upload -->
            <div v-else class="mb-2 imgUpload ppv2-h-[35px] ppv2-w-[35px]" />

            <!-- Texte principal -->
            <div v-if="!preview && !file && !isPdf && !isVideo">
                <div class="poppinsSemiBold ppv2-text-center ppv2-text-black" :style="{ fontSize: normalizeFontSize(13) }">
                    <span class="ppv2-text-[#0075FF] poppinsSemiBold">{{ $t('experience.step2.importFile1') }}</span>
                    {{ $t('experience.step2.importFile2') }}<br />
                </div>
                <span class="poppinsRegular ppv2-text-[#a3a3a3]" :style="{ fontSize: normalizeFontSize(12) }">
                    {{ requiredTypeFileText || $t('experience.step2.requiredTypeFile') }}
                </span>
                <p
                    v-if="specificTaille"
                    class="poppinsRegular ppv2-text-[#a3a3a3] !ppv2-mb-0"
                    :style="{ fontSize: normalizeFontSize(12) }"
                >
                    {{ specificTaille }}
                </p>
                <p
                    v-if="recommendedDimensions"
                    class="poppinsRegular ppv2-text-[#a3a3a3] !ppv2-mb-0"
                    :style="{ fontSize: normalizeFontSize(12) }"
                >
                    {{ $t('experience.step2.requiredTypeFileDimensions', { dimensions: recommendedDimensions }) }}
                </p>
            </div>

            <!-- Nom du fichier -->
            <span
                v-if="file || isPdf || isVideo"
                class="poppinsSemiBold ppv2-text-center ppv2-text-black ppv2-mt-2"
                :style="{ fontSize: '11px' }"
            >
                {{ file ? file.name : isRemoteImage ? value.split('/').pop() : '' }}
            </span>

            <!-- Bouton de suppression (X) — masqué si disabled -->
            <div
                v-if="(preview || file || isPdf || isVideo) && !disabled"
                class="cursor-pointer ppv2-absolute ppv2-right-2 ppv2-top-2 ppv2-z-50 ppv2-h-[25px] ppv2-w-[25px] hover:ppv2-bg-gray-100 dark:hover:ppv2-bg-gray-700 ppv2-rounded-full ppv2-transition-colors"
                @click.stop="clearFile"
            >
                <X color="#505D92" :size="15" />
            </div>

            <!-- Bouton IA -->
            <div v-if="showImproveWithAI">
                <div v-if="!isPdf && !isVideo" class="icon ia" @click.stop="improveWithAI">
                    <span class="ia-generator ppv2-text-black" :style="{ fontSize: normalizeFontSize(13) }">
                        Générer avec l’IA
                    </span>
                    <span v-if="!isDark" class="isFocused" />
                    <img v-if="isDark" src="/media/svg/aiDark.svg" alt="AI Dark" />
                </div>
            </div>

            <!-- Input natif caché -->
            <input
                ref="fileInput"
                type="file"
                :accept="accept"
                :multiple="multiple"
                hidden
                :disabled="disabled"
                @change="onFileChange"
            />
            <!--<span v-if="state === 'valid'" class="icon valid">✔</span>-->

            <!-- ⚠️ Error Icon -->
            <span v-if="computedState === 'error'" class="icon error">!</span>
        </div>
        <p v-if="(errorText || localError) && computedState === 'error'" class="error-text">
            {{ localError || (Array.isArray(errorText) ? errorText[0] : errorText) }}
        </p>
        <!-- Cropper Modal -->
        <CustomDialog v-model="showCropper" :defaultTitle="$t('common.cropImage')" width="800px" @cancel="showCropper = false">
            <template #content>
                <div v-if="imageUrl" class="ppv2-flex ppv2-gap-2 ppv2-flex-wrap">
                    <BaseButton
                        type="button"
                        v-for="option in aspectOptions"
                        :key="option.label"
                        @click="selectAspectRatio(option)"
                        customClass="py-1 px-3"
                        :customHeight="40"
                        :variant="[
                            selectedAspectRatio === option.value && isCircleSelected === !!option.circle ? 'primary' : 'outline',
                        ]"
                    >
                        {{ $t(option.label) }}
                    </BaseButton>
                </div>

                <div class="ppv2-grid ppv2-grid-cols-1 md:ppv2-grid-cols-2 ppv2-mt-4" :style="{ gap: normalizeSize(16, true) }">
                    <div
                        v-if="!imageUrl"
                        class="ppv2-col-span-2 ppv2-border ppv2-border-gray-200 ppv2-rounded ppv2-p-2 ppv2-bg-gray-50"
                    >
                        <h3 class="ppv2-text-sm poppinsMedium ppv2-mb-2">Aperçu</h3>
                        <img :src="preview" class="ppv2-w-full ppv2-h-[250px] ppv2-object-contain ppv2-rounded" />
                    </div>
                    <template v-else>
                        <cropper
                            ref="cropper"
                            :src="imageUrl"
                            class="ppv2-w-full ppv2-h-[400px] ppv2-border ppv2-border-gray-200 ppv2-rounded-md"
                            :stencil-component="isCircleSelected ? 'circle-stencil' : undefined"
                            :stencil-props="{ aspectRatio: selectedAspectRatio }"
                            :transformations.sync="transformations"
                            :wheel-resize-mode="'zoom'"
                            :auto-zoom="true"
                            @change="onCropChange"
                        />
                        <div class="ppv2-border ppv2-border-gray-200 ppv2-rounded-md ppv2-p-2 ppv2-bg-gray-50">
                            <h3 class="ppv2-text-sm poppinsMedium ppv2-mb-2">Aperçu</h3>
                            <img :src="preview" class="ppv2-w-full ppv2-h-[250px] ppv2-object-contain ppv2-rounded" />
                        </div>
                    </template>
                </div>
            </template>
            <template #actions>
                <BaseButton variant="primary" @click="confirmCrop" v-if="imageUrl">
                    {{ $t('common.cropConfirm') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </div>
</template>

<script>
import { Cropper, CircleStencil } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import normalizeSize from '../../utils/normalizeSize'
import BaseLabel from '../Shared/BaseLabel.vue'
import CustomDialog from '../Shared/CustomDialog.vue'
import BaseButton from '../Shared/BaseButton.vue'
import { X } from 'lucide-vue'
import normalizeFontSize from '../../utils/normalizeFontSize'
export default {
    name: 'CustomInputUpload',
    components: { BaseLabel, Cropper, CircleStencil, CustomDialog, BaseButton, X },
    props: {
        label: String,
        specificTaille: String,
        value: {
            type: [File, String, null],
            default: null,
        },
        description: String,
        errorText: [String, Array],
        required: Boolean,
        accept: { type: String, default: 'image/*,application/pdf' },
        multiple: { type: Boolean, default: false },
        customSizeLabel: {
            type: Number,
            default: 15,
        },
        customClassLabel: String,
        classContainerAdd: String,
        state: {
            type: String,
            default: 'default',
        },
        enableCropper: { type: Boolean, default: false },
        maxSize: { type: Number, default: 1 * 1024 * 1024 }, // 1Mo par défaut
        showImproveWithAI: { type: Boolean, default: false },
        requiredTypeFileText: { type: String, default: '' },
        recommendedDimensions: { type: String, default: '' },
        disabled: { type: Boolean, default: false },
    },
    data() {
        const isUrl = typeof this.value === 'string' && this.value
        return {
            file: isUrl ? null : this.value || null,
            preview: isUrl ? this.value : null,
            dragOver: false,
            showCropper: false,
            imageUrl: '',
            transformations: {},
            selectedAspectRatio: 1,
            isCircleSelected: false,
            isPdf: isUrl && (this.value.toLowerCase().match(/\.pdf(\?|$)/) || this.value.startsWith('data:application/pdf')),
            isVideo:
                isUrl && (this.value.toLowerCase().match(/\.(mp4|webm|ogg|mov)(\?|$)/) || this.value.startsWith('data:video/')),
            localError: null,
            aspectOptions: [
                { label: 'format.square', value: 1 },
                { label: 'format.free', value: null },
                { label: 'format.portrait', value: 3 / 4 },
                { label: 'format.landscape', value: 4 / 3 },
                { label: 'format.facebookCover', value: 820 / 312 },
            ],
        }
    },
    mounted() {},
    methods: {
        normalizeFontSize,
        normalizeSize,
        selectFile() {
            if (this.disabled) return
            this.$refs.fileInput.click()
        },
        onFileChange(event) {
            const files = event.target.files
            if (this.multiple) {
                Array.from(files).forEach((file) => this.handleFile(file))
            } else {
                this.handleFile(files[0])
            }
        },
        onDrop(event) {
            if (this.disabled) return
            const file = event.dataTransfer.files[0]
            this.dragOver = false
            this.handleFile(file)
        },
        handleFile(file) {
            if (!file) return
            // Vérifie la taille
            this.localError = null
            if (file.size > this.maxSize) {
                const sizeInMb = (this.maxSize / (1024 * 1024)).toFixed(0)
                this.localError = `Le fichier dépasse ${sizeInMb} Mo`
                return
            }

            this.isPdf = file.type === 'application/pdf'
            this.isVideo = file.type.startsWith('video/')

            if (this.isPdf || this.isVideo) {
                this.file = file
                this.preview = URL.createObjectURL(file)
                this.$emit('input', file)
                return
            }

            // Only process as image if it's actually an image file
            if (this.enableCropper && file.type.startsWith('image/')) {
                const reader = new FileReader()
                reader.onload = (e) => {
                    this.imageUrl = e.target.result
                    this.showCropper = true
                }
                reader.readAsDataURL(file)
                this.fileName = file.name // Store filename for later
            } else if (file.type.startsWith('image/')) {
                this.file = file
                this.$emit('input', file)
                const reader = new FileReader()
                reader.onload = (e) => {
                    this.preview = e.target.result
                }
                reader.readAsDataURL(file)
            } else {
                // For non-image, non-PDF files
                this.file = file
                this.preview = null
                this.$emit('input', file)
            }
        },
        onCropChange({ canvas }) {
            if (canvas) {
                this.preview = canvas.toDataURL()
            }
        },
        confirmCrop() {
            const canvas = this.$refs.cropper.getResult().canvas
            canvas.toBlob((blob) => {
                const file = new File([blob], this.fileName || 'cropped-image.webp', { type: 'image/webp' })
                this.preview = URL.createObjectURL(file)
                this.file = file
                this.$emit('input', file)
                this.showCropper = false
            }, 'image/webp')
        },
        selectAspectRatio(option) {
            this.selectedAspectRatio = option.value
            this.isCircleSelected = !!option.circle
        },
        async improveWithAI() {
            console.log('🚀 Amélioration IA déclenchée')
        },
        clearFile() {
            if (this.disabled) return
            this.file = null
            this.preview = null
            this.isPdf = false
            this.isVideo = false
            this.imageUrl = ''
            this.localError = null
            this.$emit('input', null)
            // Reset input file to allow re-uploading the same file
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
            }
        },
    },
    computed: {
        isDark() {
            return this.theme.mode === 'dark'
        },
        computedState() {
            if (this.localError) return 'error'
            if (this.state === 'default' && this.errorText) {
                if (Array.isArray(this.errorText) && this.errorText.length > 0) {
                    return 'error'
                }
                if (typeof this.errorText === 'string' && this.errorText.trim() !== '') {
                    return 'error'
                }
            }
            return this.state
        },
        isRemoteImage() {
            return typeof this.value === 'string' && this.value.startsWith('http')
        },
    },
    watch: {
        value(newVal) {
            if (newVal instanceof File) {
                this.file = newVal
                this.isPdf = newVal.type === 'application/pdf'
                this.isVideo = newVal.type.startsWith('video/')

                if (this.isPdf) {
                    this.preview = null
                } else {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        this.preview = e.target.result
                    }
                    reader.readAsDataURL(newVal)
                }
            } else if (typeof newVal === 'string' && newVal) {
                this.isPdf = newVal.toLowerCase().match(/\.pdf(\?|$)/) || newVal.startsWith('data:application/pdf')
                this.isVideo = newVal.toLowerCase().match(/\.(mp4|webm|ogg|mov)(\?|$)/) || newVal.startsWith('data:video/')

                this.preview = newVal
                this.file = null
            } else {
                this.file = null
                this.preview = null
                this.isPdf = false
                this.isVideo = false
            }
        },
    },
}
</script>

<style scoped>
.custom-upload-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 20px;
    opacity: 0.7;
    transition: opacity 0.3s ease;
}

.custom-upload-wrapper.is-cropper-open {
    opacity: 1 !important;
    z-index: 10000;
    position: relative;
}

.ppv2-opacity-100,
.ppv2-opacity-100 * {
    opacity: 1 !important;
}

.isFocused {
    background-image: url('/media/svg/aiDark.svg');
    width: 15px;
    height: 12px;
    background-size: contain;
}

.custom-upload-wrapper:focus-within .isFocused,
.custom-upload-wrapper:hover .isFocused {
    background-image: url('/media/bo/icons/ai/ai.svg');
}

.custom-upload-wrapper:focus-within,
.custom-upload-wrapper:hover {
    opacity: 1;
}

.upload-box {
    background: rgba(244, 250, 254, 0.05);
    border: 1.5px dashed #9c9af880;
    border-radius: 12px;
    color: #bcbcbc;
    padding: 1rem;
    text-align: center;
    cursor: pointer;
    position: relative;
    transition: all 0.3s ease;
    height: 147px;
    overflow: hidden;
}

.upload-box.drag-over {
    border-color: #9c9af8b2;
    background: rgba(156, 154, 248, 0.08);
}

/*.upload-box.borderValid {
    border-color: #31d0aa;
}*/

.upload-box.borderError {
    border-color: #ed4b9e;
}

.remove-btn:hover {
    transform: scale(1.1);
}

.preview-image {
    width: auto;
    height: 100%;
    object-fit: contain;
    border-radius: 10px;
}

.icon.ia {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    position: absolute;
    bottom: 8px;
    right: 12px;
    top: auto;
    font-size: 0.8rem;
    opacity: 0.7;
}

.ia-generator {
    opacity: 0.7;
    cursor: pointer;
}

.icon.ia img {
    width: 18px;
    height: 18px;
    opacity: 0.8;
    transition: opacity 0.2s;
}

.icon.ia img:hover {
    opacity: 1;
}

.dark .icon.ia img {
    filter: brightness(0.8);
}

.icon.ia img {
    width: 18px;
    height: 18px;
    opacity: 0.8;
    transition: opacity 0.2s;
}

.icon {
    background-color: transparent;
    font-size: 1rem;
    position: absolute;
    right: 16px;
    bottom: 16px;
}

/*.icon.valid {
    color: #31d0aa;
}*/

.icon.error {
    display: inline-block;
    color: #ed4b9e;
    background-color: initial !important;
    border: 1px solid;
    border-radius: 50%;
    width: 17px;
    height: 17px;
    text-align: center;
    line-height: 17px;
    font-size: 15px;
}

.error-text {
    color: #ed4b9e;
    font-size: clamp(10px, 0.625vw, 22px);
}
/* bloc if is disabled */
.upload-box.is-disabled {
    cursor: not-allowed;
    background: #f5f5f5; /* Light gray background */
    border-color: #d9d9d9;
    opacity: 0.6;
    pointer-events: none; /* Disables all internal clicks including the 'X' */
}
/* Optional: remove blue color from links when disabled */
.upload-box.is-disabled .ppv2-text-\[\#0075FF\] {
    color: #a3a3a3 !important;
}

/*bloc when disabled*/
</style>
